/**
 * The mark beside a verdict that moved on its own.
 *
 * ONE COMPONENT AND NOT THREE, because the sentence has to be identical in the
 * three places a verdict is read: the brain's own list, the follow-up queue, and
 * the history tab. Two of those are triage lists an operator scans in seconds,
 * and a caution worded differently in each is a caution nobody learns to read.
 *
 * WHY THIS EXISTS. The comparison beside it is a ruler: it reads two runs'
 * stored measurements and only ever claims a repair the newer run stood behind.
 * The verdict is not. It is a model's synthesis over findings the comparison
 * deliberately excludes, rerun from scratch each time, and free to land
 * somewhere else on identical input. Production proved that is not theoretical
 * (plan section 20.9): `romu.ee` runs 3 and 4 found the same four defects over
 * the same 59 answered addresses, 0 fixed and 0 appeared, and the pill still
 * went from "Kontrolli enne helistamist" to "Tasub helistada". An operator
 * scanning the list reads that as a company that has improved.
 *
 * ⚠ IT IS A CAUTION, NOT A CORRECTION. The verdict is not overwritten and must
 * not be: `reasons`, `segment`, `stopFlags` and every sales argument belong to
 * the new synthesis, so carrying the old decision forward would put "Tasub
 * helistada" above reasoning written to argue the opposite. That contradiction
 * gets read out loud on a call. This says how much weight the pill has earned
 * and leaves the run internally consistent.
 *
 * The colour is deliberately the muted one rather than a warning. Nothing has
 * gone wrong; the operator is being told which of two things beside each other
 * was measured and which was decided.
 */
import { ArrowLeftRight } from "lucide-react";
import { cn } from "@wa/ui";
import type { BrainVerdictDrift } from "../../../../lib/siteBrain";
import type { T } from "../../site-scanner/types";

export function VerdictDriftMark({
  drift,
  className,
  t,
}: {
  /** Null on almost every row. Drawing nothing is the common case. */
  drift: BrainVerdictDrift | null | undefined;
  className?: string;
  t: T;
}) {
  if (!drift) return null;

  // The whole sentence rides in the tooltip and only the icon takes width. Both
  // lists that show this are already dividing four tracks at a 220px floor, and
  // the one thing a row must always manage to say is which company it is.
  const title = t("brain.verdictDrift.hint", {
    from: t(`brain.fit.decision.${drift.from}`),
    to: t(`brain.fit.decision.${drift.to}`),
  });

  return (
    <span
      className={cn("crm-mut shrink-0", className)}
      title={title}
      aria-label={title}
    >
      <ArrowLeftRight className="size-3.5" />
    </span>
  );
}
